import { inject, injectable } from "inversify";
import {
  CPLServiceChargeInvoicingClient,
  GuidSelectModel,
  ReportURLResponseModel,
  ServiceChargeInvoiceProcessSelectionsModel,
} from "./api/cplapi/CPLAPI";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { ServiceChargeInvoiceAttachmentsModel } from "./classes/ServiceChargeInvoiceAttachmentsModel";

export interface IServiceChargeInvoicingService {
  getServiceChargeDateCodes(): Promise<GuidSelectModel[]>;
  resumeProcess(): Promise<ServiceChargeInvoiceProcessSelectionsModel>;
  saveProcess(selectionsModel: ServiceChargeInvoiceProcessSelectionsModel): Promise<void>;
  calculateDevelopmentChargesPreListBackgroundProcess(
    selectionsModel: ServiceChargeInvoiceProcessSelectionsModel
  ): Promise<void>;
  generateInvoiceModelsBackgroundProcess(selectionsModel: ServiceChargeInvoiceProcessSelectionsModel): Promise<void>;
  generateReportPreview(selectionsModel: ServiceChargeInvoiceProcessSelectionsModel): Promise<string | undefined>;
  acceptInvoices(): Promise<void>;
  printInvoices(selectionsModel: ServiceChargeInvoiceProcessSelectionsModel): Promise<ReportURLResponseModel>;
  emailInvoices(selectionsModel: ServiceChargeInvoiceProcessSelectionsModel): Promise<void>;
  completeServiceChargeInvoicingWizard(selectionsModel: ServiceChargeInvoiceProcessSelectionsModel): Promise<void>;
  addInvoiceAttachments(attachmentsModel: ServiceChargeInvoiceAttachmentsModel): Promise<void>;
}

@injectable()
export class ServiceChargeInvoicingService implements IServiceChargeInvoicingService {
  @inject(SERVICE_TYPES.CPLServiceChargeInvoicingClient)
  serviceChargeInvoicingClient!: CPLServiceChargeInvoicingClient;

  async getServiceChargeDateCodes(): Promise<GuidSelectModel[]> {
    return this.serviceChargeInvoicingClient.getServiceChargeDateCodes();
  }

  async resumeProcess(): Promise<ServiceChargeInvoiceProcessSelectionsModel> {
    return this.serviceChargeInvoicingClient.resumeProcess();
  }

  async saveProcess(selectionsModel: ServiceChargeInvoiceProcessSelectionsModel): Promise<void> {
    return this.serviceChargeInvoicingClient.saveProcess(selectionsModel);
  }

  async calculateDevelopmentChargesPreListBackgroundProcess(
    selectionsModel: ServiceChargeInvoiceProcessSelectionsModel
  ): Promise<void> {
    return this.serviceChargeInvoicingClient.calculateDevelopmentChargesPreListBackgroundProcess(selectionsModel);
  }

  async generateInvoiceModelsBackgroundProcess(
    selectionsModel: ServiceChargeInvoiceProcessSelectionsModel
  ): Promise<void> {
    return this.serviceChargeInvoicingClient.generateServiceChargeInvoicesBackgroundProcess(selectionsModel);
  }

  async generateReportPreview(
    selectionsModel: ServiceChargeInvoiceProcessSelectionsModel
  ): Promise<string | undefined> {
    return (await this.serviceChargeInvoicingClient.generateReportPreview(selectionsModel)).previewURL;
  }

  async acceptInvoices(): Promise<void> {
    return this.serviceChargeInvoicingClient.acceptInvoices();
  }

  async printInvoices(selectionsModel: ServiceChargeInvoiceProcessSelectionsModel): Promise<ReportURLResponseModel> {
    return this.serviceChargeInvoicingClient.printInvoices(selectionsModel);
  }

  async emailInvoices(selectionsModel: ServiceChargeInvoiceProcessSelectionsModel): Promise<void> {
    return this.serviceChargeInvoicingClient.emailInvoices(selectionsModel);
  }

  async addInvoiceAttachments(attachmentsModel: ServiceChargeInvoiceAttachmentsModel): Promise<void> {
    return this.serviceChargeInvoicingClient.uploadInvoiceAttachments(attachmentsModel);
  }

  async completeServiceChargeInvoicingWizard(
    selectionsModel: ServiceChargeInvoiceProcessSelectionsModel
  ): Promise<void> {
    return this.serviceChargeInvoicingClient.completeServiceChargeInvoicingWizard(selectionsModel);
  }
}
